import { useSession } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { Badge } from "components/ui/badge";
import {
  FolderOpen,
  ArrowSquareOut,
  Warning,
  CaretRight,
  CaretDown,
  Images,
  ArrowsClockwise,
  CheckCircle,
} from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

interface FolderNode {
  id: number;
  name: string;
  sharePointId: string;
  sharePointUrl: string;
  level: number;
  children: FolderNode[];
}

interface FolderStructure {
  hasFolders: boolean;
  mainFolder: { id: string; url: string } | null;
  picturesFolder: { id: string; url: string } | null;
  folderTree: FolderNode[];
}

interface FolderStructureTabProps {
  buildingId: string;
}

const FolderTreeNode = ({
  folder,
  level = 0,
}: {
  folder: FolderNode;
  level?: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(level < 2);
  const hasChildren = folder.children && folder.children.length > 0;

  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-neutral-50 cursor-pointer`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        {hasChildren ? (
          isExpanded ? (
            <CaretDown className="h-4 w-4 text-neutral-400 flex-shrink-0" />
          ) : (
            <CaretRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
          )
        ) : (
          <div className="w-4 flex-shrink-0" />
        )}
        <FolderOpen
          className="h-4 w-4 text-moss-primary flex-shrink-0"
          weight="duotone"
        />
        <span className="text-sm text-neutral-700 flex-grow truncate">
          {folder.name}
        </span>
        {folder.sharePointUrl && (
          <a
            href={folder.sharePointUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-moss-primary hover:text-moss-700 transition-colors"
            title="Openen in SharePoint"
          >
            <ArrowSquareOut className="h-4 w-4" />
          </a>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {folder.children.map((child) => (
            <FolderTreeNode key={child.id} folder={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

export const FolderStructureTab = ({ buildingId }: FolderStructureTabProps) => {
  const { session } = useSession();
  const [folderStructure, setFolderStructure] =
    useState<FolderStructure | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecreating, setIsRecreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFolderStructure = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/folder-structure`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error("Kon mappenstructuur niet ophalen");
      }

      const data = await response.json();
      setFolderStructure(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Onbekende fout opgetreden";
      setError(errorMessage);
      Sentry.captureException(err, {
        tags: {
          operation: "fetch_folder_structure",
          building_id: buildingId,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecreateFolders = async () => {
    try {
      setIsRecreating(true);

      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/recreate-folder-structure`,
        { method: "POST" },
        session
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Kon mappen niet aanmaken");
      }

      const result = await response.json();

      if (result.mainFolderCreated || result.picturesFolderCreated) {
        toast.success("Mappenstructuur succesvol aangemaakt!");
      } else {
        toast.info("Alle mappen bestonden al");
      }

      // Refresh the folder structure
      await fetchFolderStructure();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Onbekende fout opgetreden";
      toast.error(`Fout bij aanmaken mappen: ${errorMessage}`);
      Sentry.captureException(err, {
        tags: {
          operation: "recreate_folder_structure",
          building_id: buildingId,
        },
      });
    } finally {
      setIsRecreating(false);
    }
  };

  useEffect(() => {
    if (session && buildingId) {
      fetchFolderStructure();
    }
  }, [session, buildingId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-neutral-500">
          Mappenstructuur laden...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Warning className="h-12 w-12 text-red-500" weight="duotone" />
        <p className="text-neutral-600">{error}</p>
        <Button variant="outline" onClick={fetchFolderStructure}>
          Opnieuw proberen
        </Button>
      </div>
    );
  }

  const hasMissingFolders =
    !folderStructure?.mainFolder || !folderStructure?.picturesFolder;

  return (
    <div className="space-y-6">
      {/* Header with status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-neutral-800">
            Mappenstructuur
          </h2>
          {folderStructure?.hasFolders ? (
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              Actief
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
              <Warning className="h-3.5 w-3.5 mr-1" />
              Niet aangemaakt
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchFolderStructure}
            disabled={isLoading}
          >
            <ArrowsClockwise className="h-4 w-4 mr-1" />
            Vernieuwen
          </Button>
          {hasMissingFolders && (
            <Button
              variant="default"
              size="sm"
              onClick={handleRecreateFolders}
              disabled={isRecreating}
            >
              {isRecreating ? (
                <>
                  <Spinner className="h-4 w-4 mr-1" />
                  Bezig...
                </>
              ) : (
                <>
                  <FolderOpen className="h-4 w-4 mr-1" />
                  Mappen aanmaken
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Main folders section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Main folder card */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-moss-50">
              <FolderOpen className="h-5 w-5 text-moss-primary" weight="duotone" />
            </div>
            <div>
              <h3 className="font-medium text-neutral-800">Hoofdmap</h3>
              <p className="text-xs text-neutral-500">
                Documenten en bestanden
              </p>
            </div>
          </div>
          {folderStructure?.mainFolder ? (
            <a
              href={folderStructure.mainFolder.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-moss-primary hover:text-moss-700 transition-colors"
            >
              <ArrowSquareOut className="h-4 w-4" />
              Openen in SharePoint
            </a>
          ) : (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Warning className="h-4 w-4" />
              Niet aangemaakt
            </div>
          )}
        </div>

        {/* Pictures folder card */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <Images className="h-5 w-5 text-blue-600" weight="duotone" />
            </div>
            <div>
              <h3 className="font-medium text-neutral-800">Foto's map</h3>
              <p className="text-xs text-neutral-500">
                Afbeeldingen en foto's
              </p>
            </div>
          </div>
          {folderStructure?.picturesFolder ? (
            <a
              href={folderStructure.picturesFolder.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-moss-primary hover:text-moss-700 transition-colors"
            >
              <ArrowSquareOut className="h-4 w-4" />
              Openen in SharePoint
            </a>
          ) : (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Warning className="h-4 w-4" />
              Niet aangemaakt
            </div>
          )}
        </div>
      </div>

      {/* Folder tree section */}
      {folderStructure?.folderTree && folderStructure.folderTree.length > 0 && (
        <div className="border rounded-lg bg-white">
          <div className="px-4 py-3 border-b">
            <h3 className="font-medium text-neutral-800">Submappen</h3>
            <p className="text-xs text-neutral-500">
              Volledige mappenstructuur voor dit gebouw
            </p>
          </div>
          <div className="p-2 max-h-[400px] overflow-y-auto">
            {folderStructure.folderTree.map((folder) => (
              <FolderTreeNode key={folder.id} folder={folder} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state for folder tree */}
      {(!folderStructure?.folderTree ||
        folderStructure.folderTree.length === 0) &&
        folderStructure?.hasFolders && (
          <div className="border rounded-lg bg-white p-6 text-center">
            <FolderOpen className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500 text-sm">
              Geen submappen gevonden voor dit gebouw
            </p>
          </div>
        )}

      {/* Help text when no folders exist */}
      {!folderStructure?.hasFolders && (
        <div className="border border-amber-200 rounded-lg bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <Warning className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">
                Mappenstructuur ontbreekt
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                Er is nog geen mappenstructuur aangemaakt voor dit gebouw. Dit
                kan gebeuren als er een fout optrad tijdens de acquisitie of als
                het gebouw handmatig werd toegevoegd.
              </p>
              <p className="text-sm text-amber-700 mt-2">
                Klik op <strong>"Mappen aanmaken"</strong> om de mappenstructuur
                alsnog aan te maken in SharePoint.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderStructureTab;
